/**
 * Painel de acompanhamento da adesão ao boleto único (#5579) e disparo do aviso em ondas
 * (#5578).
 *
 * Responde as quatro perguntas do Felipe numa tela só — quem já é adepto, quem não
 * aceitou, se o disparo ocorreu, quem tem débito automático pendente — e uma quinta que
 * é o motivo de existir: **qual a próxima ação nesta UC**. As regras são puras e testadas
 * (`src/lib/boletoUnico/painelAdesao.ts` e `supabase/functions/_shared/boletoUnicoAviso.ts`);
 * aqui só tem leitura de dados e desenho.
 *
 * DISPARO (#5578): a pessoa escolhe a onda na tela e confirma. Nada sai daqui direto — a
 * função `enfileirar_aviso_boleto_unico` agenda e a edge function `processar-fila-boleto-unico`
 * manda, uma mensagem a cada 2 a 3 minutos. O resultado de cada UC, inclusive o erro, volta
 * para esta tela pela tabela `boleto_unico_disparos`.
 *
 * POR QUE A CLASSIFICAÇÃO ACONTECE NO CLIENTE, E NÃO NUMA VIEW SQL:
 *   A regra de débito automático foi auditada em 38 PDFs reais (#5570) e vive em TypeScript
 *   testado. Reescrevê-la em SQL criaria duas verdades que divergem na primeira correção.
 *   O custo é o payload: ~650 UCs elegíveis e ~2,3 mil faturas da janela, em selects
 *   enxutos — poucas centenas de KB, uma vez por abertura da tela. Se a base crescer uma
 *   ordem de grandeza, o caminho é mover a AGREGAÇÃO para SQL (contagens por UC) e manter
 *   a classificação aqui, não duplicar a regra.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  LayoutList, Search, RefreshCw, Download, AlertTriangle, CheckCircle2,
  Clock, Send, FileWarning, ExternalLink, Info, Megaphone, Square, X,
} from 'lucide-react';
import { useAuth } from '../../src/contexts/AuthContext';
import { useToast } from '../../src/contexts/ToastContext';
import { supabase } from '../../src/lib/supabase';
import { logAction, logUsageEvent } from '../../src/lib/logger';
import { formatDate } from '../../src/lib/utils';
import LoadingSpinner from '../../src/components/LoadingSpinner';
import ConfirmModal from '../../src/components/ConfirmModal';
import { buildSheet, downloadWorkbook, fileStamp } from '../../src/lib/excelExport';
import UCManagementModal from './UCManagementModal';
import { rotuloCanal } from '../../src/lib/boletoUnico/aceite';
import {
  contarGrupos,
  contarParceirasForaDoPainel,
  filtrarPainel,
  mesesReferenciaDaJanela,
  montarPainelAdesao,
  podeDisparar,
  resumirCanaisOnda,
  resumirPainel,
  rotuloCanalDisparo,
  GRUPOS_FILTRO,
  MOTIVOS_DEBITO,
  MOTIVOS_FORA_DA_ONDA,
  ROTULOS_DEBITO,
  ROTULOS_FILA,
  type AceiteAtivoPainel,
  type FalhaDisparoPainel,
  type FilaAvisoPainel,
  type GrupoFiltroPainel,
  type LinhaPainelAdesao,
  type UcPainelAdesao,
} from '../../src/lib/boletoUnico/painelAdesao';
import {
  descreverDuracao,
  lerModoDisparo,
  LIMITE_ONDA,
  minutosParaConcluirOnda,
  type CanalAviso,
  type ModoDisparo,
} from '../../supabase/functions/_shared/boletoUnicoAviso.ts';
import type { FaturaDebitoAutomatico } from '../../src/lib/boletoUnico/debitoAutomaticoEquatorial';
import type { UnidadeConsumidora } from '../../types';

/** Janela de competências avaliada para o débito automático — a mesma da #5570. */
const MESES_JANELA = 3;

/** O PostgREST corta em 1000 linhas por resposta; pagina até esgotar. */
const PASSO = 1000;

/** Com aviso na fila, confere a cada minuto se algum saiu — o mesmo ritmo do cron. */
const INTERVALO_ACOMPANHAMENTO_MS = 60_000;

const TOM_FILA: Record<string, string> = {
  perigo: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/20',
  alerta: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20',
  acao: 'bg-primary/10 text-primary border-primary/20 dark:bg-primary/20 dark:text-primary-light dark:border-primary/30',
  espera: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/20',
  ok: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20',
};

const TOM_DEBITO: Record<string, string> = {
  confirmado_sim: 'text-red-600 dark:text-red-400',
  confirmado_nao: 'text-emerald-600 dark:text-emerald-400',
  desconhecido: 'text-amber-600 dark:text-amber-400',
};

const ROTULO_CANAL_PREVISTO: Record<CanalAviso, string> = {
  whatsapp: 'vai por WhatsApp',
  email: 'vai por e-mail',
  nenhum: 'sem canal',
};

const MODO_VISUAL: Record<ModoDisparo, { texto: string; ajuda: string; classe: string }> = {
  desligado: {
    texto: 'Aviso desligado',
    ajuda: 'Nenhum aviso pode ser disparado. Liga quando a emissão do boleto novo e o aviso de aceite do Portal estiverem no ar (documento Fase 2 do projeto).',
    classe: 'bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700',
  },
  piloto: {
    texto: 'Aviso em piloto · ondas de até 10',
    ajuda: 'Onda 0 do documento Fase 2: UCs da equipe e clientes próximos, de 5 a 10 por vez.',
    classe: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20',
  },
  liberado: {
    texto: 'Aviso liberado · ondas de até 100',
    ajuda: 'Ondas seguintes do documento Fase 2. Pare se mais de 2% da onda bloquear ou denunciar o número.',
    classe: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20',
  },
};

/** Data + hora curtas. O painel é fila de trabalho: "quando" importa, "há quanto tempo" não. */
const dataHora = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${formatDate(iso)} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
};

const hora = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

/** 'AAAA-MM-DD' → 'DD/MM/AAAA' sem passar por Date (evita o recuo de um dia por fuso). */
const dataSimples = (v?: string | null): string => {
  const m = (v || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '—';
};

/** Tabela ausente: a migration 20260914130000 ainda não rodou. O painel segue, o disparo não. */
const tabelaAusente = (erro: any): boolean =>
  erro?.code === 'PGRST205' || erro?.code === '42P01';

const PainelAdesaoBoletoUnicoView: React.FC = () => {
  const { profile } = useAuth();
  const { addToast } = useToast();

  const [linhas, setLinhas] = useState<LinhaPainelAdesao[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [grupo, setGrupo] = useState<GrupoFiltroPainel>('todos');
  const [busca, setBusca] = useState('');
  const [ucAberta, setUcAberta] = useState<UnidadeConsumidora | null>(null);
  const [abrindoUc, setAbrindoUc] = useState<number | null>(null);
  // A UCManagementModal avisa alteração de campo por `onUcFieldsUpdate` (é assim que o
  // toggle de boleto único e o registro de aceite reportam), não por `onSave`. Marcamos e
  // recarregamos ao fechar: recarregar a cada campo custaria todas as UCs + faturas.
  const [ucAlterada, setUcAlterada] = useState(false);

  const [parceirasFora, setParceirasFora] = useState(0);
  const [modo, setModo] = useState<ModoDisparo>('desligado');
  const [disparoIndisponivel, setDisparoIndisponivel] = useState(false);
  const [selecionadas, setSelecionadas] = useState<Set<number>>(new Set());
  const [confirmandoOnda, setConfirmandoOnda] = useState(false);
  const [enfileirando, setEnfileirando] = useState(false);
  const [confirmandoParada, setConfirmandoParada] = useState(false);
  const [parando, setParando] = useState(false);

  // Congelado no carregamento: com `new Date()` dentro do render, a janela e a vigência
  // poderiam mudar entre a tabela e os contadores durante a vida da página.
  const [hoje, setHoje] = useState(() => new Date());
  const mesesAvaliados = useMemo(() => mesesReferenciaDaJanela(hoje, MESES_JANELA), [hoje]);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    const agora = new Date();
    setHoje(agora);
    try {
      const meses = mesesReferenciaDaJanela(agora, MESES_JANELA);

      // 1) UCs — elegibilidade e canal são da lib, então trazemos todas e ela corta.
      // Select enxuto: a tabela tem ~90 colunas e o painel usa 16.
      const ucs: UcPainelAdesao[] = [];
      for (let de = 0; ; de += PASSO) {
        const { data, error } = await supabase
          .from('unidades_consumidoras')
          .select('id, numero_uc, outro_numero_uc, nome_cliente, cpf_cnpj_cliente, situacao, empresa_parceira_id, boleto_unico, boleto_unico_aceite_em, boleto_unico_aceite_canal, boleto_unico_disparo_em, boleto_unico_disparo_canal, whatsapp_cliente, whatsapp_cliente_2, email_cliente, receber_cobranca_email')
          .order('id', { ascending: true })
          .range(de, de + PASSO - 1);
        if (error) throw error;
        if (!data?.length) break;
        ucs.push(...(data as UcPainelAdesao[]));
        if (data.length < PASSO) break;
      }

      // 2) Faturas da janela. `mes_referencia` é texto livre ('JUL/2026') e não aceita
      // comparação de intervalo — daí a lista exata de competências.
      const faturas: FaturaDebitoAutomatico[] = [];
      for (let de = 0; ; de += PASSO) {
        const { data, error } = await supabase
          .from('faturas')
          .select('uc_codigo, uc_codigo_norm, mes_referencia, debito_automatico_equatorial, linha_digitavel_equatorial')
          .in('mes_referencia', meses)
          .is('excluido_em', null)
          .order('id', { ascending: true })
          .range(de, de + PASSO - 1);
        if (error) throw error;
        if (!data?.length) break;
        faturas.push(...(data as FaturaDebitoAutomatico[]));
        if (data.length < PASSO) break;
      }

      // 3) Aceites ativos, fila de envio, falhas e o modo — independentes entre si.
      const [aceitesRes, filaRes, falhasRes, modoRes] = await Promise.all([
        supabase
          .from('boleto_unico_aceites')
          .select('uc_id, aceito_em, canal, vigencia_a_partir_de, cancelamento_atestado_em, cancelamento_confirmado_em')
          .is('revogado_em', null),
        supabase
          .from('boleto_unico_disparo_fila')
          .select('uc_id, status, scheduled_at, lote_id')
          .in('status', ['pendente', 'enviando']),
        // Só as falhas; o sucesso já está no espelho da UC. Mais recentes primeiro — a lib
        // guarda só a última por UC.
        supabase
          .from('boleto_unico_disparos')
          .select('uc_id, enviado_em, erro, canal')
          .eq('ok', false)
          .order('enviado_em', { ascending: false })
          .limit(PASSO),
        supabase
          .from('system_settings')
          .select('value')
          .eq('key', 'boleto_unico_disparo_modo')
          .maybeSingle(),
      ]);
      if (aceitesRes.error) throw aceitesRes.error;

      // A fila é a única peça que pode faltar (front no ar antes da migration). O painel
      // continua útil sem ela; só o disparo fica travado, e a tela diz por quê.
      const filaFalhou = Boolean(filaRes.error);
      if (filaRes.error && !tabelaAusente(filaRes.error)) {
        console.error('Fila do aviso de boleto único:', filaRes.error);
      }
      if (falhasRes.error) console.error('Falhas de disparo:', falhasRes.error);

      setDisparoIndisponivel(filaFalhou);
      setModo(lerModoDisparo(modoRes.data?.value));
      setParceirasFora(contarParceirasForaDoPainel(ucs));

      const montadas = montarPainelAdesao(
        ucs,
        faturas,
        (aceitesRes.data || []) as AceiteAtivoPainel[],
        { hoje: agora, meses: MESES_JANELA },
        {
          fila: (filaRes.data || []) as FilaAvisoPainel[],
          falhas: (falhasRes.data || []) as FalhaDisparoPainel[],
        },
      );
      setLinhas(montadas);

      // Seleção que deixou de valer (a UC saiu da fila de disparo) cai sozinha.
      const disparaveis = new Set(montadas.filter(podeDisparar).map(l => l.uc_id));
      setSelecionadas(prev => new Set([...prev].filter(id => disparaveis.has(id))));
    } catch (e: any) {
      console.error('Painel de adesão ao boleto único:', e);
      // 42703 = coluna inexistente. Só acontece se o front subir antes da migration
      // 20260913120000 (colunas de disparo). Mensagem de banco não diz nada para quem usa
      // a tela — diz o que fazer em vez de repetir o erro do PostgREST.
      const faltaMigration = e?.code === '42703' || /boleto_unico_disparo/.test(e?.message || '');
      // Mensagem de banco (SQL, coluna, código Postgrest) não diz nada para quem usa a
      // tela — o detalhe técnico já foi para o console acima; aqui só o que fazer.
      setErro(faltaMigration
        ? 'O painel depende de uma atualização do banco que ainda não foi aplicada (registro de disparo). Avise o time de Tecnologia.'
        : 'Não foi possível carregar o painel. Tente de novo em instantes; se persistir, avise o time de Tecnologia.');
      addToast('Erro ao carregar o painel de adesão.', 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => { carregar(); }, [carregar]);

  const contagem = useMemo(() => contarGrupos(linhas), [linhas]);
  const resumo = useMemo(() => resumirPainel(linhas), [linhas]);
  const visiveis = useMemo(() => filtrarPainel(linhas, grupo, busca), [linhas, grupo, busca]);

  const naFila = useMemo(() => linhas.filter(l => l.disparo_agendado_para), [linhas]);
  const proximoEnvio = useMemo(
    () => naFila.map(l => l.disparo_agendado_para!).sort()[0] ?? null,
    [naFila],
  );
  const ultimoEnvio = useMemo(
    () => naFila.map(l => l.disparo_agendado_para!).sort().at(-1) ?? null,
    [naFila],
  );

  // Acompanhamento leve: com aviso na fila, a cada minuto só conta o que ainda falta sair.
  // Recarrega a tela inteira só quando esse número muda — senão seriam todas as UCs e
  // faturas por minuto só para ver o mesmo estado.
  useEffect(() => {
    if (naFila.length === 0 || disparoIndisponivel) return;
    const timer = setInterval(async () => {
      const { count, error } = await supabase
        .from('boleto_unico_disparo_fila')
        .select('id', { count: 'exact', head: true })
        .in('status', ['pendente', 'enviando']);
      if (!error && count !== naFila.length) carregar();
    }, INTERVALO_ACOMPANHAMENTO_MS);
    return () => clearInterval(timer);
  }, [naFila.length, disparoIndisponivel, carregar]);

  const limiteOnda = LIMITE_ONDA[modo];
  const disparoLigado = limiteOnda > 0 && !disparoIndisponivel;

  const linhasSelecionadas = useMemo(
    () => linhas.filter(l => l.uc_id !== undefined && selecionadas.has(l.uc_id)),
    [linhas, selecionadas],
  );
  const canaisOnda = useMemo(() => resumirCanaisOnda(linhasSelecionadas), [linhasSelecionadas]);
  const visiveisDisparaveis = useMemo(() => visiveis.filter(podeDisparar), [visiveis]);
  const todasVisiveisSelecionadas = visiveisDisparaveis.length > 0
    && visiveisDisparaveis.slice(0, limiteOnda).every(l => l.uc_id !== undefined && selecionadas.has(l.uc_id));

  const alternarSelecao = (ucId?: number) => {
    if (ucId === undefined) return;
    setSelecionadas(prev => {
      const nova = new Set(prev);
      if (nova.has(ucId)) {
        nova.delete(ucId);
      } else if (nova.size >= limiteOnda) {
        addToast(`No modo atual a onda tem no máximo ${limiteOnda} UCs.`, 'warning');
        return prev;
      } else {
        nova.add(ucId);
      }
      return nova;
    });
  };

  // "Selecionar todas" pega as visíveis na ordem da tabela, até o limite — e diz quando
  // cortou, para ninguém achar que a onda levou a lista inteira.
  const alternarTodasVisiveis = () => {
    if (todasVisiveisSelecionadas) {
      const visiveisIds = new Set(visiveisDisparaveis.map(l => l.uc_id));
      setSelecionadas(prev => new Set([...prev].filter(id => !visiveisIds.has(id))));
      return;
    }
    const nova = new Set(selecionadas);
    for (const l of visiveisDisparaveis) {
      if (nova.size >= limiteOnda) break;
      if (l.uc_id !== undefined) nova.add(l.uc_id);
    }
    if (visiveisDisparaveis.length > limiteOnda) {
      addToast(`Selecionadas as primeiras ${limiteOnda} — é o tamanho máximo da onda no modo atual.`, 'info');
    }
    setSelecionadas(nova);
  };

  const dispararOnda = async () => {
    setEnfileirando(true);
    try {
      const ids = [...selecionadas];
      const { data, error } = await supabase.rpc('enfileirar_aviso_boleto_unico', { p_uc_ids: ids });
      if (error) throw error;

      const resultado = data as { enfileiradas: number; ultima_em: string | null; ignoradas: { motivo: string }[] };
      const foraPorMotivo = new Map<string, number>();
      for (const i of resultado.ignoradas || []) {
        foraPorMotivo.set(i.motivo, (foraPorMotivo.get(i.motivo) || 0) + 1);
      }
      const fora = [...foraPorMotivo].map(([motivo, n]) => `${n} ${MOTIVOS_FORA_DA_ONDA[motivo] || motivo}`).join(', ');

      if (resultado.enfileiradas > 0) {
        addToast(
          `${resultado.enfileiradas} aviso(s) na fila. O último sai por volta das ${hora(resultado.ultima_em)}.${fora ? ` Ficaram de fora: ${fora}.` : ''}`,
          'success',
          8000,
        );
      } else {
        addToast(`Nenhum aviso entrou na fila.${fora ? ` Motivo: ${fora}.` : ''}`, 'warning', 8000);
      }

      await logAction(profile?.id, 'DISPARAR_AVISO_BOLETO_UNICO', {
        solicitadas: ids.length,
        enfileiradas: resultado.enfileiradas,
        ignoradas: resultado.ignoradas,
        modo,
      });
      logUsageEvent({
        userId: profile?.id, modulo: 'financeiro',
        funcionalidade: 'adesao_boleto_unico', acao: 'disparar_aviso', origem: 'acao',
      });

      setSelecionadas(new Set());
      setConfirmandoOnda(false);
      await carregar();
    } catch (e: any) {
      console.error('Enfileirar aviso do boleto único:', e);
      // As mensagens da função já saem em português ("O disparo do aviso está desligado.",
      // "A onda tem 12 UCs; no modo piloto o máximo é 10.").
      addToast(e?.message || 'Não foi possível disparar o aviso.', 'error', 8000);
    } finally {
      setEnfileirando(false);
    }
  };

  const pararEnvios = async () => {
    setParando(true);
    try {
      const { data, error } = await supabase.rpc('cancelar_avisos_pendentes_boleto_unico', { p_lote_id: null });
      if (error) throw error;
      addToast(`${data ?? 0} aviso(s) parado(s) antes de sair. O que já estava sendo enviado termina normalmente.`, 'success', 8000);
      await logAction(profile?.id, 'PARAR_AVISO_BOLETO_UNICO', { cancelados: data });
      setConfirmandoParada(false);
      await carregar();
    } catch (e: any) {
      console.error('Parar avisos do boleto único:', e);
      addToast(e?.message || 'Não foi possível parar os envios.', 'error');
    } finally {
      setParando(false);
    }
  };

  // O painel não edita a UC: a ação mora na UCManagementModal, onde o aceite e a liberação
  // já existem (#5576). Buscar a UC completa no clique evita carregar 90 colunas × 650
  // linhas só porque o modal precisa delas em uma.
  const abrirUc = async (ucId?: number) => {
    if (!ucId) return;
    setAbrindoUc(ucId);
    try {
      const { data, error } = await supabase
        .from('unidades_consumidoras')
        .select('*')
        .eq('id', ucId)
        .single();
      if (error) throw error;
      setUcAberta(data as UnidadeConsumidora);
    } catch (e: any) {
      console.error('Erro ao abrir a UC:', e);
      addToast('Não foi possível abrir esta UC.', 'error');
    } finally {
      setAbrindoUc(null);
    }
  };

  const situacaoDoAviso = (l: LinhaPainelAdesao): string => {
    if (l.disparo_em) return `Enviado à UaisyChat em ${dataHora(l.disparo_em)}`;
    if (l.disparo_agendado_para) return `Agendado para ${dataHora(l.disparo_agendado_para)}`;
    if (l.falha_disparo_em) return `Falhou em ${dataHora(l.falha_disparo_em)}: ${l.falha_disparo_erro}`;
    return 'Não disparado';
  };

  const exportar = () => {
    const headers = [
      'Cliente', 'UC', 'Outro nº UC', 'Situação', 'Aviso', 'Canal do disparo', 'Canal previsto',
      'Aceite em', 'Canal do aceite', 'Vigência a partir de', 'Débito automático',
      'Motivo', 'Último mês avaliado', 'Liberado no boleto único', 'Próxima ação', 'O que fazer',
    ];
    const rows = visiveis.map(l => [
      l.cliente, l.uc_codigo, l.outro_numero_uc || '', l.situacao || '',
      situacaoDoAviso(l),
      l.disparo_em ? rotuloCanalDisparo(l.disparo_canal) : '',
      l.disparo_em ? '' : (l.motivo_sem_canal || ROTULO_CANAL_PREVISTO[l.canal_aviso]),
      l.aceite_em ? dataHora(l.aceite_em) : 'Sem aceite',
      l.aceite_em ? rotuloCanal(l.aceite_canal) : '',
      dataSimples(l.vigencia_a_partir_de),
      ROTULOS_DEBITO[l.debito], MOTIVOS_DEBITO[l.debito_motivo],
      l.ultimo_mes_avaliado || '',
      l.boleto_unico ? 'Sim' : 'Não',
      ROTULOS_FILA[l.fila].texto, ROTULOS_FILA[l.fila].ajuda,
    ]);
    const ws = buildSheet(
      headers, rows,
      Array(headers.length).fill('txt'),
      { widths: [32, 14, 14, 14, 44, 18, 30, 18, 18, 16, 14, 46, 14, 12, 22, 60] },
    );
    downloadWorkbook(
      [{ name: 'Adesão Boleto Único', ws }],
      `Adesao_Boleto_Unico_${fileStamp()}.xlsx`,
    );
    logUsageEvent({
      userId: profile?.id, modulo: 'financeiro',
      funcionalidade: 'adesao_boleto_unico', acao: 'exportar', origem: 'acao',
    });
  };

  const cartoes = [
    { rotulo: 'UCs elegíveis', valor: resumo.elegiveis, cor: 'text-gray-900 dark:text-white', icone: LayoutList,
      ajuda: 'Ativas, em ciclo ou reativadas, da própria Vigor. Cancelada, pausada e UC de parceira não entram.' },
    { rotulo: 'Já no boleto único', valor: resumo.liberadas, cor: 'text-emerald-600 dark:text-emerald-400', icone: CheckCircle2,
      ajuda: 'Cobrando Vigor + Equatorial no mesmo documento.' },
    { rotulo: 'Aceitaram, não liberadas', valor: resumo.aceitaram_nao_liberadas, cor: 'text-blue-600 dark:text-blue-400', icone: Clock,
      ajuda: 'Aceite registrado e adesão ainda desligada.' },
    { rotulo: 'Precisam de atenção', valor: resumo.atencao,
      cor: resumo.atencao > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-300 dark:text-gray-600', icone: AlertTriangle,
      ajuda: 'Cobrança em dobro possível, ou adesão ligada sem aceite do cliente.' },
  ];

  const colunas = disparoLigado ? 8 : 7;
  const modoVisual = MODO_VISUAL[modo];

  return (
    <>
      <div className={`animate-fade-in space-y-6 ${selecionadas.size > 0 ? 'pb-40' : 'pb-24'}`}>
        {/* Cabeçalho */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h2 className="text-2xl font-poppins font-bold dark:text-white flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center shadow-lg shadow-primary/20">
                <LayoutList size={20} className="text-white" />
              </div>
              Adesão ao Boleto Único
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 ml-[52px] max-w-3xl">
              Quem já aceitou, quem foi avisado, quem tem débito automático na Equatorial e o que
              falta em cada UC. Quem não aceitou continua recebendo boleto da Vigor e fatura da
              Equatorial separados.
            </p>
          </div>

          {!loading && !erro && (
            <span
              className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border whitespace-nowrap ${
                disparoIndisponivel ? MODO_VISUAL.desligado.classe : modoVisual.classe
              }`}
              title={disparoIndisponivel
                ? 'O disparo de aviso ainda não está disponível neste ambiente. O painel funciona normalmente; avise o time de Tecnologia antes de tentar disparar.'
                : modoVisual.ajuda}
            >
              <Megaphone size={13} aria-hidden="true" />
              {disparoIndisponivel ? 'Aviso indisponível' : modoVisual.texto}
            </span>
          )}
        </div>

        {/* Premissas da leitura — sem isso os números não têm como ser conferidos. */}
        <div className="flex flex-wrap items-start gap-2 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3">
          <Info size={14} className="mt-0.5 shrink-0 text-gray-400" aria-hidden="true" />
          <div className="space-y-1">
            <p>
              Débito automático avaliado nas faturas de{' '}
              <strong className="font-semibold text-gray-700 dark:text-gray-200">
                {[...mesesAvaliados].reverse().join(', ')}
              </strong>
              {' '}— a conclusão vale para essa janela, não é carimbo permanente na UC. A adesão vale para
              faturas de <strong className="font-semibold text-gray-700 dark:text-gray-200">OUT/2026</strong>{' '}
              em diante: liberar antes disso faz o boleto do mês corrente já sair unificado.
            </p>
            {parceirasFora > 0 && (
              <p>
                <strong className="font-semibold text-gray-700 dark:text-gray-200">{parceirasFora} UCs de empresa parceira</strong>{' '}
                ficam fora deste painel: a Gran tem boleto único próprio, pelo fluxo dela.
              </p>
            )}
          </div>
        </div>

        {/* Cartões */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {cartoes.map(c => (
            <div key={c.rotulo} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm" title={c.ajuda}>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                <c.icone size={13} aria-hidden="true" /> {c.rotulo}
              </p>
              <p className={`text-2xl font-bold mt-1 ${c.cor}`} aria-live="polite">
                {loading ? '—' : c.valor}
              </p>
            </div>
          ))}
        </div>

        {/* Onda em andamento: sempre visível enquanto houver aviso por sair, com o botão de
            parar ao lado — é o "Quando parar" do documento Fase 2 a um clique. */}
        {!loading && naFila.length > 0 && (
          <div
            role="status"
            className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 rounded-xl px-4 py-3"
          >
            <p className="text-sm text-blue-800 dark:text-blue-300 flex items-start gap-2">
              <Send size={15} className="mt-0.5 shrink-0" aria-hidden="true" />
              <span>
                <strong className="font-semibold">{naFila.length} aviso(s) na fila de envio.</strong>{' '}
                Próximo às {hora(proximoEnvio)}; o último deve sair por volta das {hora(ultimoEnvio)}.
              </span>
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setGrupo('na_fila')}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-500/20 transition-colors whitespace-nowrap"
              >
                Ver quem está na fila
              </button>
              <button
                onClick={() => setConfirmandoParada(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-white dark:bg-gray-800 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-500/30 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors whitespace-nowrap"
              >
                <Square size={12} aria-hidden="true" /> Parar envios
              </button>
            </div>
          </div>
        )}

        {/* Filtros */}
        <div className="flex flex-col gap-3">
          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:items-center">
            <div className="relative flex-1 min-w-[220px]">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true" />
              <input
                value={busca}
                onChange={e => setBusca(e.target.value)}
                placeholder="Buscar por cliente ou UC..."
                aria-label="Buscar por cliente ou número da UC"
                className="w-full pl-9 pr-3 py-2.5 text-sm rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>

            <button
              onClick={carregar}
              className="p-2 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              title="Atualizar"
              aria-label="Atualizar o painel"
            >
              <RefreshCw size={16} className={`text-gray-500 dark:text-gray-400 ${loading ? 'animate-spin' : ''}`} />
            </button>

            <button
              onClick={exportar}
              disabled={visiveis.length === 0}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold bg-primary/10 text-primary hover:bg-primary/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
              title="Exporta exatamente as linhas visíveis, com o motivo de cada status"
            >
              <Download size={14} aria-hidden="true" />
              Exportar Excel
            </button>
          </div>

          {/* Filas de trabalho. O contador vem da mesma função que filtra — não há como
              o chip dizer 12 e a tabela mostrar 9. */}
          <div className="flex flex-wrap items-center gap-1 bg-white dark:bg-gray-800 p-1 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
            {GRUPOS_FILTRO.map(g => {
              const ativo = grupo === g.id;
              const total = contagem[g.id] ?? 0;
              if (g.somenteComItens && total === 0 && !ativo) return null;
              const destaque = (g.id === 'atencao' || g.id === 'falha_disparo') && total > 0;
              return (
                <button
                  key={g.id}
                  onClick={() => setGrupo(g.id)}
                  aria-pressed={ativo}
                  title={g.ajuda}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap ${
                    ativo
                      ? 'bg-primary text-white shadow-sm'
                      : destaque
                        ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10'
                        : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {destaque && <AlertTriangle size={13} aria-hidden="true" />}
                  {g.rotulo}
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                    ativo ? 'bg-white/20' : 'bg-gray-100 dark:bg-gray-700'
                  }`}>
                    {loading ? '—' : total}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Tabela */}
        {loading ? (
          <LoadingSpinner label="Cruzando UCs, aceites, avisos e faturas..." />
        ) : erro ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-red-200 dark:border-red-500/20 p-8 text-center">
            <FileWarning size={26} className="mx-auto text-red-500 mb-3" aria-hidden="true" />
            <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">Não foi possível montar o painel.</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{erro}</p>
            <button
              onClick={carregar}
              className="mt-4 px-4 py-2 rounded-lg text-xs font-semibold bg-primary text-white hover:bg-primary-dark transition-colors"
            >
              Tentar de novo
            </button>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
            <div className="overflow-auto custom-scrollbar max-h-[70vh]">
              <table className="w-full text-left border-collapse min-w-[1120px]">
                <caption className="sr-only">
                  UCs elegíveis ao boleto único, com aviso, aceite, débito automático e a próxima ação de cada uma.
                </caption>
                <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-900 border-b border-gray-100 dark:border-gray-700">
                  <tr>
                    {disparoLigado && (
                      <th scope="col" className="px-4 py-4 w-10">
                        <input
                          type="checkbox"
                          checked={todasVisiveisSelecionadas}
                          ref={el => {
                            // Nem tudo nem nada: alguma UC visível marcada, mas não todas (ex.: o
                            // limite da onda cortou a seleção). Sem isso a caixa "some" desmarcada
                            // e passa a impressão de que a seleção anterior se perdeu.
                            if (el) el.indeterminate = !todasVisiveisSelecionadas
                              && visiveisDisparaveis.some(l => l.uc_id !== undefined && selecionadas.has(l.uc_id));
                          }}
                          disabled={visiveisDisparaveis.length === 0}
                          onChange={alternarTodasVisiveis}
                          className="rounded text-primary focus:ring-primary disabled:opacity-30"
                          aria-label={`Selecionar as UCs visíveis que podem receber o aviso, até ${limiteOnda}`}
                          title={visiveisDisparaveis.length === 0
                            ? 'Nenhuma UC visível pode receber o aviso agora'
                            : `Seleciona até ${limiteOnda} UCs visíveis que ainda não receberam o aviso`}
                        />
                      </th>
                    )}
                    {['Cliente / UC', 'Aviso', 'Aceite', 'Débito automático', 'Boleto único', 'Próxima ação', ''].map((h, i) => (
                      <th
                        key={h || `acao-${i}`}
                        scope="col"
                        className={`px-6 py-4 text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider ${i === 6 ? 'text-right' : ''}`}
                      >
                        {h || <span className="sr-only">Ação</span>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                  {visiveis.length === 0 ? (
                    <tr>
                      <td colSpan={colunas} className="px-6 py-12 text-center text-gray-400 dark:text-gray-500 text-sm">
                        {linhas.length === 0
                          ? 'Nenhuma UC elegível encontrada.'
                          : busca.trim()
                            ? `Nenhuma UC nesta fila com "${busca.trim()}".`
                            : 'Nada nesta fila — nenhuma pendência deste tipo hoje.'}
                      </td>
                    </tr>
                  ) : visiveis.map(l => {
                    const fila = ROTULOS_FILA[l.fila];
                    const selecionavel = disparoLigado && podeDisparar(l);
                    const marcada = l.uc_id !== undefined && selecionadas.has(l.uc_id);
                    return (
                      <tr
                        key={l.uc_id ?? l.uc_codigo}
                        className={`transition-colors ${marcada ? 'bg-primary/5 dark:bg-primary/10' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}
                      >
                        {disparoLigado && (
                          <td className="px-4 py-4 w-10">
                            {selecionavel && (
                              <input
                                type="checkbox"
                                checked={marcada}
                                onChange={() => alternarSelecao(l.uc_id)}
                                className="rounded text-primary focus:ring-primary"
                                aria-label={`Incluir ${l.cliente} (UC ${l.uc_codigo}) na onda`}
                              />
                            )}
                          </td>
                        )}

                        <td className="px-6 py-4">
                          <div className="flex flex-col gap-0.5">
                            <span className="text-sm font-bold text-gray-900 dark:text-white">{l.cliente || '—'}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              UC {l.uc_codigo || '—'}
                              {l.outro_numero_uc && <span className="text-gray-400"> · {l.outro_numero_uc}</span>}
                              {l.situacao && <span className="text-gray-400"> · {l.situacao}</span>}
                            </span>
                          </div>
                        </td>

                        <td className="px-6 py-4 max-w-[260px]">
                          {l.disparo_em ? (
                            <div
                              className="flex flex-col gap-0.5"
                              title={l.disparo_canal === 'whatsapp'
                                ? 'A UaisyChat recebeu o pedido. A entrega no WhatsApp acontece depois, lá dentro — se o cliente disser que não recebeu, confira a conversa na UaisyChat.'
                                : undefined}
                            >
                              <span className="text-xs font-semibold text-gray-700 dark:text-gray-200 flex items-center gap-1">
                                <Send size={11} className="text-gray-400" aria-hidden="true" /> {dataHora(l.disparo_em)}
                              </span>
                              <span className="text-[11px] text-gray-400">
                                {l.disparo_canal === 'whatsapp' ? 'WhatsApp · enviado à UaisyChat' : rotuloCanalDisparo(l.disparo_canal)}
                              </span>
                            </div>
                          ) : l.disparo_agendado_para ? (
                            <div className="flex flex-col gap-0.5">
                              <span className="text-xs font-semibold text-blue-700 dark:text-blue-400 flex items-center gap-1">
                                <Clock size={11} aria-hidden="true" /> Agendado · {dataHora(l.disparo_agendado_para)}
                              </span>
                              <span className="text-[11px] text-gray-400">{ROTULO_CANAL_PREVISTO[l.canal_aviso]}</span>
                            </div>
                          ) : l.falha_disparo_em ? (
                            <div className="flex flex-col gap-0.5">
                              <span className="text-xs font-semibold text-amber-700 dark:text-amber-400 flex items-center gap-1">
                                <AlertTriangle size={11} aria-hidden="true" /> Falhou · {dataHora(l.falha_disparo_em)}
                              </span>
                              <span className="text-[11px] text-gray-500 dark:text-gray-400 line-clamp-2" title={l.falha_disparo_erro || undefined}>
                                {l.falha_disparo_erro}
                              </span>
                            </div>
                          ) : (
                            <div className="flex flex-col gap-0.5">
                              <span className="text-xs text-gray-400">Não disparado</span>
                              <span
                                className={`text-[11px] ${l.canal_aviso === 'nenhum' ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400'}`}
                                title={l.motivo_sem_canal || undefined}
                              >
                                {ROTULO_CANAL_PREVISTO[l.canal_aviso]}
                              </span>
                            </div>
                          )}
                        </td>

                        <td className="px-6 py-4">
                          {l.aceite_em ? (
                            <div className="flex flex-col gap-0.5">
                              <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">{dataHora(l.aceite_em)}</span>
                              <span className="text-[11px] text-gray-400">{rotuloCanal(l.aceite_canal)}</span>
                              {!l.vigencia_iniciada && (
                                <span className="text-[11px] text-blue-600 dark:text-blue-400">
                                  vale a partir de {dataSimples(l.vigencia_a_partir_de)}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">Sem aceite</span>
                          )}
                        </td>

                        <td className="px-6 py-4">
                          <span
                            className={`text-xs font-semibold ${TOM_DEBITO[l.debito]}`}
                            title={MOTIVOS_DEBITO[l.debito_motivo]}
                          >
                            {ROTULOS_DEBITO[l.debito]}
                          </span>
                          <span className="block text-[11px] text-gray-400">
                            {l.ultimo_mes_avaliado ? `última fatura lida: ${l.ultimo_mes_avaliado}` : 'sem fatura na janela'}
                          </span>
                          {l.cancelamento_atestado_em && (
                            <span className="block text-[11px] text-emerald-600 dark:text-emerald-400">
                              cancelamento atestado em {dataHora(l.cancelamento_atestado_em)}
                            </span>
                          )}
                        </td>

                        <td className="px-6 py-4">
                          <span className={`text-xs font-semibold ${l.boleto_unico ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400'}`}>
                            {l.boleto_unico ? 'Liberado' : 'Não'}
                          </span>
                        </td>

                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex items-center text-[11px] font-semibold px-2 py-1 rounded-md border ${TOM_FILA[fila.tom]}`}
                            title={fila.ajuda}
                          >
                            {fila.texto}
                          </span>
                          <span className="block text-[11px] text-gray-400 mt-1 max-w-[320px]">{fila.ajuda}</span>
                        </td>

                        <td className="px-6 py-4 text-right">
                          <button
                            onClick={() => abrirUc(l.uc_id)}
                            disabled={abrindoUc === l.uc_id}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-primary bg-primary/10 hover:bg-primary/20 disabled:opacity-50 transition-colors whitespace-nowrap"
                            title="Abrir a UC para corrigir o contato, registrar aceite, atestar cancelamento ou ligar o boleto único"
                            aria-label={`Abrir a UC ${l.uc_codigo} de ${l.cliente}`}
                          >
                            <ExternalLink size={13} aria-hidden="true" />
                            {abrindoUc === l.uc_id ? 'Abrindo...' : 'Abrir UC'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {visiveis.length > 0 && (
              <div className="px-6 py-3 border-t border-gray-100 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
                {visiveis.length} de {linhas.length} UCs elegíveis
                {grupo !== 'todos' && ` · filtro: ${GRUPOS_FILTRO.find(g => g.id === grupo)?.rotulo}`}
                {disparoLigado && visiveisDisparaveis.length > 0 && ` · ${visiveisDisparaveis.length} podem receber o aviso`}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Barra da onda: aparece com a primeira UC marcada e diz por onde cada uma vai sair
          antes de qualquer confirmação. Mesmo padrão da barra de seleção em massa de
          AuditoriaPagamentosView e CRMTable (pílula escura fixada no rodapé) — não o cartão
          claro que estava aqui antes. */}
      {selecionadas.size > 0 && disparoLigado && (
        <div className="fixed inset-x-0 bottom-0 z-40 px-3 pb-3 pointer-events-none flex justify-center">
          <div
            role="region"
            aria-label="Onda de aviso selecionada"
            className="pointer-events-auto w-full sm:w-auto bg-gray-900 dark:bg-gray-950 text-white rounded-2xl shadow-2xl border border-white/10 px-3 py-2.5 flex flex-wrap items-center gap-2 sm:gap-3 animate-fade-in"
          >
            <div className="flex items-center gap-2 pr-1">
              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary text-white text-xs font-bold shrink-0" aria-live="polite">
                {selecionadas.size}
              </span>
              <div className="leading-tight">
                <p className="text-xs font-bold whitespace-nowrap">{selecionadas.size} de {limiteOnda} na onda</p>
                <p className="text-[11px] text-white/60 whitespace-nowrap">
                  {canaisOnda.whatsapp} WhatsApp · {canaisOnda.email} e-mail
                  {canaisOnda.sem_canal > 0 && (
                    <span className="text-amber-400"> · {canaisOnda.sem_canal} sem canal</span>
                  )}
                </p>
              </div>
            </div>
            <div className="h-7 w-px bg-white/15 hidden sm:block" />
            <button
              onClick={() => setConfirmandoOnda(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-primary hover:bg-primary-dark transition-colors whitespace-nowrap"
            >
              <Megaphone size={14} aria-hidden="true" /> Disparar aviso
            </button>
            <button
              onClick={() => setSelecionadas(new Set())}
              className="p-1.5 rounded-full text-white/70 hover:bg-white/10 transition-colors"
              title="Limpar seleção"
              aria-label="Limpar seleção"
            >
              <X size={16} aria-hidden="true" />
            </button>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmandoOnda}
        onClose={() => !enfileirando && setConfirmandoOnda(false)}
        onConfirm={dispararOnda}
        loading={enfileirando}
        variant="warning"
        title={`Disparar o aviso para ${selecionadas.size} UC(s)?`}
        message="O cliente recebe o convite para aderir ao boleto único e o link do Portal. Não dá para desfazer uma mensagem que já saiu."
        confirmLabel={`Disparar para ${selecionadas.size}`}
      >
        <ul className="mt-3 space-y-1.5 text-sm text-gray-600 dark:text-gray-300">
          <li><strong className="text-gray-900 dark:text-white">{canaisOnda.whatsapp}</strong> por WhatsApp, template <code className="text-xs">vigor_boleto_unico_aviso</code></li>
          <li><strong className="text-gray-900 dark:text-white">{canaisOnda.email}</strong> por e-mail, com o mesmo texto</li>
          {canaisOnda.sem_canal > 0 && (
            <li className="text-amber-700 dark:text-amber-400">
              <strong>{canaisOnda.sem_canal}</strong> sem WhatsApp nem e-mail autorizado: não recebem nada e ficam em
              "Falha no aviso" com o motivo, para corrigir o cadastro.
            </li>
          )}
          <li className="pt-1.5 text-xs text-gray-500 dark:text-gray-400">
            Saem um a cada 2 a 3 minutos — a onda termina em {descreverDuracao(minutosParaConcluirOnda(selecionadas.size))}.
            Até lá, dá para parar o que ainda não saiu.
          </li>
        </ul>
      </ConfirmModal>

      <ConfirmModal
        isOpen={confirmandoParada}
        onClose={() => !parando && setConfirmandoParada(false)}
        onConfirm={pararEnvios}
        loading={parando}
        variant="danger"
        title="Parar os avisos que ainda não saíram?"
        message={`${naFila.length} aviso(s) na fila deixam de ser enviados. O que já saiu não volta, e a mensagem que estiver sendo enviada neste minuto termina normalmente. As UCs voltam para "Pendente de disparo".`}
        confirmLabel="Parar envios"
      />

      {/* A ação acontece onde ela já existia: aceite, atestado de cancelamento e liberação
          são da UCManagementModal (#5576). Ao fechar, recarrega — o estado pode ter mudado. */}
      <UCManagementModal
        isOpen={!!ucAberta}
        onClose={() => {
          setUcAberta(null);
          if (ucAlterada) {
            setUcAlterada(false);
            carregar();
          }
        }}
        unidade={ucAberta}
        onSave={carregar}
        onUcFieldsUpdate={() => setUcAlterada(true)}
      />
    </>
  );
};

export default PainelAdesaoBoletoUnicoView;
